import {
	subscriptionIsExpiredLast6Months,
	subscriptionIsExpiring
} from "@/helpers/device-subscription.helper.js";
import i18n from "@/lang/lang";
import { Subscription } from "./subscription";
import { ConnectivityStatus } from "./connectivity";

const CONTROL_RANGES = new Set(["CONTROL", "TOWER_CONTROL"]);

export default class Device {
	constructor(deviceResponseFromApi) {
		this.deviceId = deviceResponseFromApi.deviceId;
		this.hardwareId = deviceResponseFromApi.hardwareId;
		this.lifecycle = deviceResponseFromApi.lifecycle;
		this.name = deviceResponseFromApi.name;
		this.connectivityStatus = deviceResponseFromApi.connectivityStatus
			? new ConnectivityStatus(deviceResponseFromApi.connectivityStatus)
			: null;
		this.deviceVersionInfo =
			deviceResponseFromApi.deviceVersionInfo || null;
		this.subscription = deviceResponseFromApi.subscription
			? new Subscription(deviceResponseFromApi.subscription)
			: null;
		this.type = deviceResponseFromApi.type;
		this.description = deviceResponseFromApi.description;
		this.organizationId = deviceResponseFromApi.organizationId;
		this.tenantId = deviceResponseFromApi.tenantId;
		this.externalId = deviceResponseFromApi.externalId;
		this.openAlarms = deviceResponseFromApi.openAlarms || [];
		this.tags = deviceResponseFromApi.tags || {};

		this.isControlRange = CONTROL_RANGES.has(
			deviceResponseFromApi.subscription?.current?.ratePlanDetails?.range
		);
		this.isTowerLift =
			deviceResponseFromApi.subscription?.current?.ratePlanDetails
				?.range === "TOWER_LIFT";

		this.isRenewed = this.subscription?.isRenewed;

		this.rawDeviceFromApi = deviceResponseFromApi;
	}

	get raw() {
		return this.rawDeviceFromApi;
	}

	get status() {
		return this.connectivityStatus?.connectionStatusInfo?.status;
	}

	get qttAddons() {
		return (
			this.subscription?.qttAddons || this.subscription?.extensionsNumber
		);
	}

	get isActive() {
		return ["ACTIVATED"].includes(this.lifecycle);
	}

	get isInactive() {
		// FACTORY and READY shouldn't be displayed to user as it doesn't have an organization assigned yet (but in staging we have some cases like that, so display it)
		return ["FACTORY", "READY", "SHIPPED"].includes(this.lifecycle);
	}

	get isSubscriptionExpiring() {
		return (
			!this.subscription?.next &&
			subscriptionIsExpiring(this?.subscription?.endDate)
		);
	}

	get isSubscriptionExpired() {
		return this.subscription?.isExpired;
	}

	get isSubscriptionExpiredInLast6Months() {
		return subscriptionIsExpiredLast6Months(this?.subscription?.endDate);
	}

	get isClassicalHub() {
		return this.type === "CLASSICAL_HUB" || this.lifecycle === "HUB";
	}

	get isTower() {
		return ["TOWER", "BOX"].includes(this.type);
	}

	get isBridge() {
		return ["BRIDGE", "HUB"].includes(this.type);
	}

	get humanFriendlyTypeName() {
		switch (this.type) {
			case "TOWER":
			case "BOX":
				return i18n.t("client-name-box");
			case "BRIDGE":
			case "HUB":
			case "CLASSICAL_HUB":
				return i18n.t("client-name-hub");
			default:
				return null;
		}
	}

	get hasOpenAlarms() {
		return (this.openAlarms || []).length > 0;
	}

	get range() {
		return this.subscription?.current?.ratePlanDetails?.range;
	}

	get numberOfDataPointsPer10Minutes() {
		return this.subscription?.current?.ratePlanDetails?.config
			?.numberOfDataPointsPer10Minutes;
	}

	get maxDataPoints() {
		return this.subscription?.current?.maxDataPoints;
	}
}
